import type {
  Channel, ConsentRecord, ConsentSource, Customer, CustomerOrder, Daypart, FlagAuditEntry, OrderChannel, OriginChannel, RiskFlag,
} from './types';
import { mockItems, mockOutlets, mulberry32 } from '../../../redux/customers/customerReference';
import { namedCustomers, type NamedCustomer, type OrderPattern } from './mockNamedCustomers';

/**
 * Seed directory for the frontend-only build: the hand-written customers plus generated ones.
 * Order dates are relative to `now`, so recency-based segments always have people in them.
 */
const DAY_MS = 86_400_000;
const TOTAL_CUSTOMERS = 200;

const FIRST_NAMES = ['Maya', 'Arjun', 'Priya', 'Daniel', 'Sofia', 'Liam', 'Ananya', 'Noah', 'Emma', 'Ravi', 'Olivia', 'Karthik', 'Ava', 'Ethan', 'Meera', 'Lucas', 'Isabella', 'Vikram', 'Mia', 'James', 'Divya', 'Henry', 'Zara', 'Samuel', 'Lakshmi'];
const LAST_NAMES = ['Patel', 'Nguyen', 'Garcia', 'Iyer', 'Johnson', 'Kim', 'Reddy', 'Martinez', 'Chen', 'Williams', 'Nair', 'Brown', 'Singh', 'Lopez', 'Rao', 'Davis', 'Menon', 'Wilson', 'Khan', 'Anderson'];
const CHANNEL_WEIGHTS: [OrderChannel, number][] = [['dine_in', 34], ['takeout', 20], ['online', 18], ['delivery', 12], ['app', 9], ['kiosk', 5], ['catering', 2]];
const DAYPART_WEIGHTS: [Daypart, number][] = [['lunch', 40], ['dinner', 38], ['breakfast', 16], ['late_night', 6]];
const CONSENT_SOURCES: ConsentSource[] = ['pos', 'online_ordering', 'customer_app', 'kiosk', 'cfd'];
const PATTERN_WEIGHTS: [OrderPattern, number][] = [['new', 12], ['regular', 30], ['at_risk', 18], ['lapsed', 22], ['occasional', 18]];
const ORIGIN_BY_CHANNEL: Record<OrderChannel, OriginChannel> = {
  dine_in: 'pos', takeout: 'pos', kiosk: 'pos', catering: 'pos', delivery: 'online_qr', online: 'online_qr', app: 'app',
};

export function buildMockCustomers(now: Date = new Date(), count = TOTAL_CUSTOMERS): Customer[] {
  const rand = mulberry32(20261002);
  const int = (min: number, max: number) => min + Math.floor(rand() * (max - min + 1));
  const chance = (p: number) => rand() < p;
  const pick = <T,>(list: T[]): T => list[Math.floor(rand() * list.length)];
  const weighted = <T,>(list: [T, number][]): T => {
    const total = list.reduce((s, [, w]) => s + w, 0);
    let r = rand() * total;
    for (const [value, w] of list) {
      r -= w;
      if (r < 0) return value;
    }
    return list[0][0];
  };
  const iso = (daysBack: number, hour = 12) => {
    const d = new Date(now.getTime() - daysBack * DAY_MS);
    d.setUTCHours(hour, int(0, 59), 0, 0);
    return d.toISOString();
  };

  const orderDays = (pattern: OrderPattern): number[] => {
    const spread = (n: number, from: number, to: number) => Array.from({ length: n }, () => int(from, to));
    switch (pattern) {
      case 'new': return [int(1, 28)];
      case 'regular': return [int(0, 12), ...spread(int(3, 12), 3, 170)];
      case 'vip': return [int(0, 6), ...spread(int(14, 22), 2, 170)];
      case 'at_risk': return [int(46, 89), ...spread(int(1, 5), 90, 300)];
      case 'lapsed': return [int(95, 320), ...spread(int(0, 4), 321, 600)];
      default: return [int(8, 44), ...spread(int(0, 2), 45, 240)];
    }
  };

  const buildOrders = (id: string, pattern: OrderPattern, preferredOutlet: string, only?: OrderChannel): CustomerOrder[] =>
    orderDays(pattern).sort((a, b) => b - a).map((d, n) => {
      const items = Array.from({ length: int(1, 3) }, () => pick(mockItems));
      const big = pattern === 'vip' || only === 'catering';
      return {
        id: `ord-${id}-${n}`,
        at: iso(d, int(8, 21)),
        total: (big ? int(3200, 14000) : int(900, 6500)) / 100,
        outletId: chance(0.75) ? preferredOutlet : pick(mockOutlets).id,
        channel: only ?? weighted(CHANNEL_WEIGHTS),
        daypart: weighted(DAYPART_WEIGHTS),
        itemIds: Array.from(new Set(items.map((it) => it.id))),
        categoryIds: Array.from(new Set(items.map((it) => it.categoryId))),
      };
    });

  let consentSeq = 0;
  const consent = (channel: Channel, status: ConsentRecord['status'], daysBack: number, source: ConsentSource): ConsentRecord => {
    consentSeq += 1;
    const at = iso(daysBack, 10);
    return {
      id: `con-${String(consentSeq).padStart(4, '0')}`, channel, status, source,
      consentTextVersion: daysBack > 200 ? 'v1.0' : 'v1.1',
      capturedAt: at, capturedBy: null, createdAt: at, updatedAt: at,
    };
  };

  /** Totals every customer record carries, taken from its orders. */
  const fromOrders = (orders: CustomerOrder[]) => ({
    orders,
    firstVisitAt: orders[0]?.at,
    lastVisitAt: orders[orders.length - 1]?.at,
    visitCount: orders.length,
    lifetimeSpend: Math.round(orders.reduce((s, o) => s + o.total, 0) * 100) / 100,
  });

  const fromNamed = (c: NamedCustomer): Customer => {
    const { seed, ...profile } = c;
    const preferredOutletId = mockOutlets[0].id;
    const orders = buildOrders(c.id, seed.pattern, preferredOutletId, seed.orderChannel);
    const firstDays = Math.round((now.getTime() - new Date(orders[0].at).getTime()) / DAY_MS);
    const app = Boolean(seed.app);
    return {
      lifecycleState: 'active', importantDates: [], favouriteItems: [], customAttributes: [], groupIds: [], savedCards: [],
      ...profile,
      ...fromOrders(orders),
      createdAt: c.createdAt < orders[0].at ? c.createdAt : orders[0].at,
      updatedAt: orders[orders.length - 1].at,
      preferredOutletId,
      appInstalled: app,
      pushEnabled: app && seed.optIn.includes('push'),
      marketingConsent: seed.optIn.map((channel) => consent(channel, 'granted', firstDays + 1, channel === 'push' ? 'customer_app' : 'pos')),
    };
  };

  const generated = (i: number): Customer => {
    const id = `cus-${2001 + i}`;
    const pattern = weighted(PATTERN_WEIGHTS);
    const preferredOutletId = pick(mockOutlets).id;
    const orders = buildOrders(id, pattern, preferredOutletId);
    const firstDays = Math.round((now.getTime() - new Date(orders[0].at).getTime()) / DAY_MS);
    const createdDays = firstDays + int(0, 3);
    const hasPhone = chance(0.92);
    const hasEmail = !hasPhone || chance(0.7);
    const firstName = pick(FIRST_NAMES);
    const lastName = pick(LAST_NAMES);
    const member = chance(0.55);
    const pushEnabled = chance(0.3);
    // Everyone with push switched on has the app; some have the app with push off.
    const appInstalled = pushEnabled || chance(0.12);
    const totals = fromOrders(orders);

    const marketingConsent: ConsentRecord[] = [];
    const source = pick(CONSENT_SOURCES);
    if (hasPhone && chance(0.66)) {
      marketingConsent.push(consent('sms', 'granted', createdDays, source));
      if (chance(0.08)) marketingConsent.push(consent('sms', 'revoked', int(0, Math.max(1, createdDays - 1)), 'sms_keyword'));
    }
    if (hasEmail && chance(0.78)) {
      marketingConsent.push(consent('email', 'granted', createdDays, source));
      if (chance(0.06)) marketingConsent.push(consent('email', 'revoked', int(0, Math.max(1, createdDays - 1)), 'email_link'));
    }
    if (pushEnabled && chance(0.85)) marketingConsent.push(consent('push', 'granted', Math.max(0, createdDays - 1), 'customer_app'));

    // A few flagged customers, so the Blocked rule has an effect on campaign audiences.
    const flagRoll = rand();
    const riskStatus = flagRoll < 0.02 ? 'blocked' : flagRoll < 0.05 ? 'watch' : 'none';
    const flagAt = iso(int(2, 40), 15);
    const activeFlag: RiskFlag | undefined = riskStatus === 'none' ? undefined : {
      status: riskStatus, reasonCode: riskStatus === 'blocked' ? 'promo_abuse' : 'no_show',
      note: riskStatus === 'blocked' ? 'Repeated misuse of promo codes across accounts.' : 'Missed two reservations without cancelling.',
      setBy: 'Aisha Khan', setAt: flagAt,
    };
    const auditLog: FlagAuditEntry[] = activeFlag
      ? [{ id: `aud-${id}`, action: 'set', fromStatus: 'none', toStatus: activeFlag.status, reasonCode: activeFlag.reasonCode, note: activeFlag.note, actor: activeFlag.setBy, timestamp: flagAt }]
      : [];

    return {
      id,
      name: `${firstName} ${lastName}`,
      phone: hasPhone ? `+1 609 555 ${String(1000 + i).slice(-4)}` : undefined,
      email: hasEmail ? `${firstName}.${lastName}${i}@example.com`.toLowerCase() : undefined,
      birthday: chance(0.6) ? `${int(1965, 2003)}-${String(int(1, 12)).padStart(2, '0')}-${String(int(1, 28)).padStart(2, '0')}` : undefined,
      tags: pattern === 'regular' && chance(0.4) ? ['Regular'] : [],
      riskStatus,
      activeFlag,
      originChannel: member && chance(0.3) ? 'loyalty' : ORIGIN_BY_CHANNEL[orders[0].channel],
      ...totals,
      preferredOutletId,
      appInstalled,
      pushEnabled,
      auditLog,
      lifecycleState: pattern === 'lapsed' && firstDays > 400 && chance(0.5) ? 'inactive' : 'active',
      importantDates: [],
      loyaltyStatus: member
        ? {
          tier: 'Member', points: int(0, 900), memberSince: iso(createdDays, 9).slice(0, 10),
          hasUnredeemedReward: chance(0.25), pointsExpiringInDays: chance(0.15) ? int(3, 40) : null,
        }
        : null,
      favouriteItems: [],
      customAttributes: [],
      groupIds: [],
      marketingConsent,
      savedCards: [],
      createdAt: iso(createdDays, 9),
      updatedAt: orders[orders.length - 1].at,
    };
  };

  const customers = [
    ...namedCustomers.map(fromNamed),
    ...Array.from({ length: Math.max(0, count - namedCustomers.length) }, (_, i) => generated(i)),
  ];

  // A likely duplicate pair for the merge flow (LNK-11): the same person, one record by phone and one by email.
  const [a, b] = customers.slice(namedCustomers.length);
  if (a && b) {
    b.name = a.name;
    a.phone = a.phone ?? '+1 609 555 0199';
    a.email = undefined;
    a.marketingConsent = a.marketingConsent.filter((r) => r.channel !== 'email');
    b.phone = undefined;
    b.email = `${a.name.replace(' ', '.')}@example.com`.toLowerCase();
    b.marketingConsent = b.marketingConsent.filter((r) => r.channel !== 'sms');
  }

  return customers;
}

export const mockCustomers: Customer[] = buildMockCustomers();
